/*
 *    Copyright [2007-2025] [wisemapping]
 *
 *   Licensed under WiseMapping Public License, Version 1.0 (the "License").
 *   It is basically the Apache License, Version 2.0 (the "License") plus the
 *   "powered by wisemapping" text requirement on every single page;
 *   you may not use this file except in compliance with the License.
 *   You may obtain a copy of the license at
 *
 *       https://github.com/wisemapping/wisemapping-open-source/blob/main/LICENSE.md
 *
 *   Unless required by applicable law or agreed to in writing, software
 *   distributed under the License is distributed on an "AS IS" BASIS,
 *   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *   See the License for the specific language governing permissions and
 *   limitations under the License.
 */

import { css, SerializedStyles } from '@emotion/react';
import { useTheme } from '@mui/material/styles';
import { useMemo } from 'react';
import { Theme } from '@mui/material/styles';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type StyleValue = Record<string, any>;

function useClasses<T extends Record<string, StyleValue>>(
  stylesElement: T | ((theme: Theme) => T),
): Record<string, SerializedStyles> {
  const theme = useTheme();

  return useMemo(() => {
    // Get the raw styles object (call function if needed)
    const rawClasses = typeof stylesElement === 'function' ? stylesElement(theme) : stylesElement;

    // Wrap each value with css()
    const prepared: Record<string, SerializedStyles> = {};
    for (const [key, value] of Object.entries(rawClasses)) {
      prepared[key] = css(value);
    }

    return prepared;
  }, [theme, stylesElement]);
}

export default useClasses;
